// The MIT License (MIT)
//
// Copyright (c) 2016-2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import angular from 'angular';
import epfl_authgeoportailBase from '../epfl_authgeoportailmodule.js';
import epfl_authgeoportailCommon from './Common.js';
import epfl_authgeoportailBacktoinvappComponent from '../backtoinvapp/component.js';
import epfl_authgeoportailFloorComponent from '../floor/component.js';
import epfl_authgeoportailSearchComponent from '../search/component.js';
import gmfAbstractMobileController, {
  AbstractMobileController,
} from 'gmf/controllers/AbstractMobileController.js';

import './sass/mobilepro.scss';

/**
 * @private
 */
class Controller extends AbstractMobileController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {angular.IHttpService} $http Angular's $http service.
   * @param {angular.IWindowService} $window The Angular $window service.
   * @param {string} loginUrl The login URL.
   * @param {string} rolesUrl The roles URL.
   * @param {string} currentInterfaceUrl The current interface URL.
   * @param {string} authenticationBaseUrl The current interface URL.
   */
  constructor(
    $scope,
    $injector,
    $http,
    $window,
    loginUrl,
    rolesUrl,
    currentInterfaceUrl,
    authenticationBaseUrl,
  ) {
    super($scope, $injector);

    /**
     * @type {Array.<Object>|undefined}
     * @export
     */
    this.availableRoles = undefined;

    $http.get(rolesUrl).then((result) => {
      this.availableRoles = result['data']['roles'].sort((a, b) => a.name.localeCompare(b.name));
    });

    /**
     * @type {string}
     * @export
     */
    this.currentInterfaceUrl = currentInterfaceUrl;

    /**
     * @type {string}
     * @export
     */
    this.authenticationBaseUrl = authenticationBaseUrl;

    /**
     * @type {angular.$window}
     * @private
     */
    this.$window_ = $window;

    /**
     * @type {angular.IHttpService}
     * @private
     */
    this.$http_ = $http;

    /**
     * @type {epfl_authgeoportail.Common}
     * @export
     */
    this.common = new epfl_authgeoportailCommon(this, $scope, $injector);

    if (this.common.enableBackToInvApp) {
      this.$http_.get(`${this.authenticationBaseUrl}loginuser`).then((response) => {
        const isLogged = response.data['username'] !== undefined;
        if (!isLogged) {
          const returnUrl = encodeURIComponent(document.location.href);
          document.location = `${loginUrl}?came_from=${returnUrl}`;
        }
      });
    }
  }

  getCurrentRoles() {
    return this.gmfUser['roles'] !== null ? this.gmfUser['roles'][0]['name'] : undefined;
  }

  /**
   * Reload the page with the corresponding role
   * * @export
   */
  changeRole(selectedRole) {
    if (!selectedRole) {
      return;
    }
    this.$http_.get(`${this.authenticationBaseUrl}loginuser?map_param_role=${selectedRole.id}`).then(() => {
      this.$window_.location.href = `${this.currentInterfaceUrl}?map_param_role=${selectedRole.id}`;
    });
  }
}
Controller.$inject = [
  '$scope',
  '$injector',
  '$http',
  '$window',
  'loginUrl',
  'rolesUrl',
  'currentInterfaceUrl',
  'authenticationBaseUrl',
];

/**
 * @hidden
 */
const gmfModule = angular.module('Appmobilepro', [
  epfl_authgeoportailBase.name,
  epfl_authgeoportailCommon.module.name,
  gmfAbstractMobileController.name,
  epfl_authgeoportailBacktoinvappComponent.name,
  epfl_authgeoportailFloorComponent.name,
  epfl_authgeoportailSearchComponent.name,
]);

gmfModule.constant('redirectUrl', '');

gmfModule.controller('MobileProController', Controller);

export default gmfModule;
